import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="playlist"
export default class extends Controller {
  static targets = ["item", "toggleButton", "playIcon", "pauseIcon", "toggleLabel", "more"]
  static outlets = ["video-player"]
  static values = {
    playing: { type: Boolean, default: false },
    selectedSectionId: Number
  }

  connect() {
    this.boundPrepareForCache = this.prepareForCache.bind(this)
    document.addEventListener("turbo:before-cache", this.boundPrepareForCache)

    this.syncActiveItemFromSelection()
    this.updateToggleButton()
    if (this.selectedSectionId() && !this.findSelectedItem()) this.loadMore()
  }

  disconnect() {
    this.pendingAdvance = false
    if (this.boundPrepareForCache) {
      document.removeEventListener("turbo:before-cache", this.boundPrepareForCache)
    }
  }

  handleItemActivated(event) {
    this.pendingAdvance = false
    this.switchingItems = true
    this.playingValue = true

    const sectionId = event.detail.sectionId
    this.selectedSectionIdValue = Number(sectionId)
    // Auth frame navigation uses this URL as its return location. Playback can
    // advance behind the dialog without changing the requested destination.
    if (!document.querySelector("#modal[open]")) this.updateSelectedSectionUrl(sectionId)
  }

  togglePlayback(event) {
    const videoPlayer = this.videoPlayerController()
    if (!videoPlayer) return

    event.preventDefault()
    videoPlayer.togglePlayPause()
  }

  handlePlaybackStarted() {
    this.pendingAdvance = false
    this.loadMore()
    this.switchingItems = false
    this.playingValue = true
  }

  handlePlaybackPaused() {
    if (this.switchingItems) return

    this.playingValue = false
  }

  handlePlaybackEnded() {
    this.handlePlaybackPaused()

    // A deep-linked moment can finish before its row arrives. Connected rows
    // handle completion themselves through playlist-item:ended.
    if (this.selectedSectionId() && !this.findSelectedItem() && this.hasMoreTarget && !this.moreTarget.hasAttribute("complete")) {
      this.pendingAdvance = true
      this.loadMore()
    }
  }

  playingValueChanged() {
    this.updateToggleButton()
  }

  selectedSectionIdValueChanged() {
    this.syncActiveItemFromSelection()
  }

  updateToggleButton() {
    if (!this.hasToggleButtonTarget) return

    this.playIconTarget.classList.toggle("hidden", this.playingValue)
    this.pauseIconTarget.classList.toggle("hidden", !this.playingValue)
    const playLabel = this.toggleButtonTarget.dataset.playlistPlayLabel
    const pauseLabel = this.toggleButtonTarget.dataset.playlistPauseLabel
    const label = this.playingValue ? pauseLabel : playLabel

    this.toggleLabelTarget.textContent = label
    this.toggleButtonTarget.setAttribute("aria-pressed", this.playingValue.toString())
    this.toggleButtonTarget.setAttribute("aria-label", label)
  }

  videoPlayerController() {
    if (this.hasVideoPlayerOutlet) return this.videoPlayerOutlet

    const player = document.querySelector("[data-controller~=video-player]")
    if (!player) return null

    return this.application.getControllerForElementAndIdentifier(player, "video-player")
  }

  signIn(event) {
    // Auth modals use the current page as their return location. Preserve the
    // requested moment in that URL without changing the playing selection.
    this.updateSelectedSectionUrl(event.params.sectionId)
    event.currentTarget.closest("[popover]").hidePopover()
  }

  itemTargetConnected() {
    this.syncActiveItemFromSelection()
  }

  loadMore() {
    if (this.hasMoreTarget) this.moreTarget.loading = "eager"
  }

  moreLoaded() {
    this.syncActiveItemFromSelection()
    if (!this.pendingAdvance) return

    this.pendingAdvance = false
    const currentItem = this.getCurrentActiveItem()
    const nextItem = currentItem && this.getNextItem(currentItem)
    if (nextItem) {
      this.activateItem(nextItem)
    } else {
      this.playlistEnded = true
      this.playingValue = false
    }
  }

  // Navigate to the next video in the playlist
  nextVideo() {
    const currentItem = this.getCurrentActiveItem()
    if (!currentItem) {
      return
    }

    const nextItem = this.getNextItem(currentItem)
    if (nextItem) {
      this.activateItem(nextItem)
    }
  }

  // Navigate to the previous video in the playlist
  previousVideo() {
    const currentItem = this.getCurrentActiveItem()
    if (!currentItem) {
      return
    }

    const previousItem = this.getPreviousItem(currentItem)
    if (previousItem) {
      this.activateItem(previousItem)
    }
  }

  // Activate a specific playlist item by index
  goToVideo(event) {
    const index = parseInt(event.params.index)
    const item = this.itemTargets[index]

    if (item) {
      this.activateItem(item)
    }
  }

  // Handle when a playlist item ends - advance to next
  handleItemEnded() {
    // Prevent multiple rapid calls
    if (this.handlingEnd) return
    this.handlingEnd = true

    setTimeout(() => {
      this.handlingEnd = false
    }, 1000)

    const currentItem = this.getCurrentActiveItem()
    if (!currentItem) {
      return
    }

    const nextItem = this.getNextItem(currentItem)
    if (nextItem) {
      this.activateItem(nextItem)
    } else if (this.hasMoreTarget && !this.moreTarget.hasAttribute("complete")) {
      this.pendingAdvance = true
      this.loadMore()
    } else {
      // Explicitly stop any further processing at playlist end
      this.playlistEnded = true
      this.playingValue = false
    }
  }

  // Handle when a playlist item starts playing - set as active
  handleItemPlaying(event) {
    const playingItem = event.detail.element

    // If playlist has ended, don't process new playing events unless user manually clicks
    if (this.playlistEnded) {
      return
    }

    this.setActiveItem(playingItem)
    this.playingValue = true
  }

  prepareForCache() {
    this.element.querySelectorAll("[popover]:popover-open").forEach((popover) => popover.hidePopover())
    this.pendingAdvance = false
    this.playingValue = false
    this.switchingItems = false
    this.handlingEnd = false
    this.syncActiveItemFromSelection()

    this.itemTargets.forEach((item) => {
      item.setAttribute("data-playlist-item-loading-value", "false")
      item.setAttribute("data-playlist-item-playing-value", "false")

      const progressBar = item.querySelector("[data-playlist-item-target='progressBar']")
      progressBar.value = 0
      progressBar.setAttribute("value", "0")
    })
  }

  // Private methods

  getCurrentActiveItem() {
    const selectedItem = this.findSelectedItem()
    if (selectedItem) return selectedItem

    // Find the currently active playlist item
    return this.itemTargets.find(
      (item) => item.classList.contains("active") || item.hasAttribute("data-active") || item.getAttribute("aria-current") === "true"
    )
  }

  getNextItem(currentItem) {
    const currentIndex = this.itemTargets.indexOf(currentItem)
    return this.itemTargets[currentIndex + 1] || null
  }

  getPreviousItem(currentItem) {
    const currentIndex = this.itemTargets.indexOf(currentItem)
    return this.itemTargets[currentIndex - 1] || null
  }

  activateItem(item) {
    // Reset playlist ended state when user manually activates an item
    this.playlistEnded = false

    // Remove active state from all items
    this.itemTargets.forEach((target) => {
      target.classList.remove("active")
      target.removeAttribute("data-active")
      target.removeAttribute("aria-current")
    })

    // Add active state to the selected item
    item.classList.add("active")
    item.setAttribute("data-active", "true")
    item.setAttribute("aria-current", "true")

    // Trigger the item's play method to load the video
    const controller = this.application.getControllerForElementAndIdentifier(item, "playlist-item")
    if (controller && controller.play) {
      controller.play()
    } else {
      // Fallback: dispatch a click event
      item.click()
    }
  }

  // Set active state without triggering playback (used when item starts playing)
  setActiveItem(item) {
    // Remove active state from all items
    this.itemTargets.forEach((target) => {
      target.classList.remove("active")
      target.removeAttribute("data-active")
      target.removeAttribute("aria-current")
    })

    // Add active state to the selected item
    item.classList.add("active")
    item.setAttribute("data-active", "true")
    item.setAttribute("aria-current", "true")
  }

  syncActiveItemFromSelection() {
    const selectedSectionId = this.selectedSectionId()
    if (!this.hasItemTarget || !selectedSectionId) return

    this.itemTargets.forEach((item) => {
      const active = item.dataset.playlistItemSectionIdValue === selectedSectionId
      item.classList.toggle("active", active)
      item.toggleAttribute("data-active", active)
      item.setAttribute("data-playlist-item-active-value", active.toString())
      item.setAttribute("data-playlist-item-playing-value", (active && this.playingValue).toString())

      if (active) {
        item.setAttribute("aria-current", "true")
      } else {
        item.removeAttribute("aria-current")
      }
    })
  }

  findSelectedItem() {
    const selectedSectionId = this.selectedSectionId()
    if (!selectedSectionId) return null

    return this.itemTargets.find((item) => item.dataset.playlistItemSectionIdValue === selectedSectionId) || null
  }

  selectedSectionId() {
    if (!this.hasSelectedSectionIdValue || this.selectedSectionIdValue <= 0) return null

    return String(this.selectedSectionIdValue)
  }

  updateSelectedSectionUrl(sectionId) {
    const url = new URL(window.location.href)
    const value = String(sectionId)
    if (url.searchParams.get("section_id") === value) return

    url.searchParams.set("section_id", value)
    window.history.replaceState(window.history.state, "", url)
  }

  // Getter for current video info
  getCurrentVideoInfo() {
    const activeItem = this.getCurrentActiveItem()
    if (!activeItem) return null

    return {
      element: activeItem,
      index: this.itemTargets.indexOf(activeItem),
      total: this.itemTargets.length
    }
  }

  // Check if there's a next video available
  hasNextVideo() {
    const currentItem = this.getCurrentActiveItem()
    if (!currentItem) return false

    const currentIndex = this.itemTargets.indexOf(currentItem)
    return currentIndex < this.itemTargets.length - 1
  }

  // Check if there's a previous video available
  hasPreviousVideo() {
    const currentItem = this.getCurrentActiveItem()
    if (!currentItem) return false

    const currentIndex = this.itemTargets.indexOf(currentItem)
    return currentIndex > 0
  }

  // Get playlist statistics
  getPlaylistStats() {
    const currentInfo = this.getCurrentVideoInfo()
    return {
      total: this.itemTargets.length,
      current: currentInfo ? currentInfo.index + 1 : 0,
      hasNext: this.hasNextVideo(),
      hasPrevious: this.hasPreviousVideo()
    }
  }
}
